import { Payment, PrepareLnurlPayResponse, PrepareSendPaymentResponse } from "@breeztech/breez-sdk-spark/web";
import { BulkTypes } from "../models/BulkPayment";
import { Pleb } from "../models/Pleb";
import { apiResponseToTransaction, PaymentMethod, sparkResponseToTransaction, Transaction, WalletType } from "../models/Transactions";
import { BulkQuoteResponse, PaymentKind, ScanData, walletRepository } from "../repositories/WalletRepository";
import { SendTransactionState, SendTransactionStateRecipient } from "../store/slices/transactionSlice";
import { formatAmount, formatAmountWithSATS } from "../utils/Utils";
import { getBreezSdk, getIdentityPubkey } from "../utils/breezSdk";
import { getTransactionIdFromSparkPayment } from "../utils/BreezSpark";

export class ScanRecipientValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ScanRecipientValidationError";
  }
}

export enum PreparePaymentResultType {
  BULK_QUOTE = "bulk_quote",
  LNURL_PREPARE = "lnurl_prepare",
  SEND_PREPARE = "send_prepare",
}

export type PreparePaymentResult =
  | { type: PreparePaymentResultType.BULK_QUOTE; data: BulkQuoteResponse, amountIncludesFees: boolean }
  | { type: PreparePaymentResultType.LNURL_PREPARE; data: PrepareLnurlPayResponse, amountIncludesFees: boolean }
  | { type: PreparePaymentResultType.SEND_PREPARE; data: PrepareSendPaymentResponse, amountIncludesFees: boolean };

export const scanRecipientAction = async (
  walletType: WalletType | null,
  recipient: SendTransactionStateRecipient,
  membersOnly: boolean | undefined,
  balance: number | null | undefined
): Promise<{ recipient: SendTransactionStateRecipient | null; scanData: ScanData | null }> => {
  if (recipient.bulkPayment) {
    const data = await walletRepository.scanBulkPayment(
      recipient.bulkPayment.type,
      recipient.bulkPayment.id,
      membersOnly,
      walletType ?? undefined,
      recipient.bulkPayment.filters ?? undefined
    );

    if (data.recipientsCount > (balance ?? 0)) {
      throw new ScanRecipientValidationError(`You need at least ${formatAmount(data.recipientsCount)} SATS to send this zap`);
    }

    return {
      recipient: {
        pleb: null,
        lnurl: "",
        bulkPayment: data,
      },
      scanData: null,
    };
  }

  switch(walletType) {
    case WalletType.SPARK:
      let paymentRequest = '';
      if(recipient?.pleb?.walletType === WalletType.SPARK && recipient?.pleb?.sparkStaticAddress)
        paymentRequest = recipient.pleb.sparkStaticAddress;
      else if(recipient?.pleb?.id)
        paymentRequest = `user:${recipient.pleb.id}`;
      else if(recipient.invoice)
        paymentRequest = recipient.invoice;
      else if(recipient.lnurl)
        paymentRequest = recipient.lnurl;
      else
        paymentRequest = "";

      const breezSdk = getBreezSdk();

      if (!breezSdk) {
        throw new Error("Breez SDK not found");
      }

      const scanResult = await walletRepository.scanSparkWallet(paymentRequest);

      if (!scanResult) {
        throw new Error("Error scanning payment request");
      }

      recipient.pleb = scanResult.user ? Pleb.currentUserFromJson(scanResult.user) : null;

      const parseResponse = await breezSdk.parse(scanResult.paymentRequest);
      let scanData: ScanData;

      switch (parseResponse.type) {
        case "sparkAddress":
          scanData = {
            kind: PaymentKind.PAY,
            paymentRequest: scanResult.paymentRequest,
            parseResponse: parseResponse,
            commentAllowed: 300,
          };
          break;
        case "lightningAddress": {
          const payRequest = parseResponse.payRequest;
          scanData = {
            kind: PaymentKind.PAY,
            paymentRequest: scanResult.paymentRequest,
            parseResponse: parseResponse,
            commentAllowed: payRequest.commentAllowed,
            domain: payRequest.domain,
            callback: payRequest.callback,
            maxSendable: payRequest.maxSendable / 1000,
            minSendable: payRequest.minSendable / 1000,
          };
          break;
        }
        case "bolt11Invoice":
          scanData = {
            kind: PaymentKind.PAY,
            paymentRequest: scanResult.paymentRequest,
            parseResponse: parseResponse,
            commentAllowed: 0,
            fixedAmountMsat: parseResponse.amountMsat,
          };
          break;
        case "bitcoinAddress":
          scanData = {
            kind: PaymentKind.PAY,
            paymentRequest: scanResult.paymentRequest,
            parseResponse: parseResponse,
            commentAllowed: 0,
          };
          break;
        default:
          console.log(parseResponse);
          throw new Error("Unsupported payment request type");
      }

      return {
        recipient: recipient,
        scanData: scanData,
      };
      break;
    case WalletType.INTERNAL:
      // Handle regular payment
      const lnurlToScan = recipient.lnurl === '' 
      ? recipient?.pleb?.zapAddress ?? '' 
      : recipient?.lnurl ?? '';
      
      const data = await walletRepository.scan(lnurlToScan);

      if (data?.walletInternal?.user) {
        recipient.pleb = Pleb.currentUserFromJson(data.walletInternal.user);
      }
      
      // Update recipient with scan data
      if (recipient.lnurl !== '') {
          recipient.lnurl = data.lnurl;
      }
      
      if (recipient.invoice !== '') {
          recipient.invoice = data.invoice;
      }
      
      // If the pleb has an encoded lnurl in the zapAddress, we need to update it
      if (recipient.pleb && recipient.pleb.shouldShowNameAndPicture() === false) {
          recipient.pleb.zapAddress = data.lnurl;
      }
      
      return {
        recipient: recipient,
        scanData: data,
      };
      break;
  }

  throw new Error("Invalid wallet type");
};

export const preparePaymentFromTransactionState = async (
  transactionState: SendTransactionState,
  amountIncludesFees: boolean = false
): Promise<PreparePaymentResult> => {
  try {
    if (transactionState.recipient?.bulkPayment) {
      const bulkPayment = transactionState.recipient.bulkPayment;
      const bulkQuote = await walletRepository.askForBulkQuote({
        fromIdentityPubkey: getIdentityPubkey(),
        network: "spark",
        type: bulkPayment.type,
        id: bulkPayment.id,
        amountMsatPerRecipient: (transactionState.amount ?? 0) * 1000,
        comment: transactionState.comment ?? "",
        anonymous: transactionState.anonymous ?? false,
        membersOnly: transactionState.membersOnly ?? false,
        imageUrl: transactionState.imageUrl ?? undefined,
        filters: bulkPayment.filters ?? undefined
      });
      return {
        type: PreparePaymentResultType.BULK_QUOTE,
        data: bulkQuote,
        amountIncludesFees: false,
      };
    } else {
      const breezSdk = getBreezSdk();

      if (!breezSdk) {
        throw new Error("Breez SDK not found");
      }

      if (!transactionState.scanData?.paymentRequest) {
        throw new Error("Payment request not found");
      }

      if (!transactionState.amount) {
        throw new Error("Amount not found");
      }

      if (transactionState.scanData.parseResponse?.type === "lightningAddress") {
        const amountSats = transactionState.amount;
        const optionalComment = transactionState.comment ?? "";
        const payRequest = transactionState.scanData.parseResponse.payRequest;

        const lnurlPrepare = await breezSdk.prepareLnurlPay({
          amount: BigInt(amountSats),
          payRequest,
          comment: optionalComment,
          feePolicy: amountIncludesFees ? "feesIncluded" : undefined,
        });
        return {
          type: PreparePaymentResultType.LNURL_PREPARE,
          data: lnurlPrepare,
          amountIncludesFees: amountIncludesFees,
        };
      } else {
        const sendPrepare = await breezSdk.prepareSendPayment({
          paymentRequest: { type: "input", input: transactionState.scanData.paymentRequest },
          amount: BigInt(transactionState.amount ?? 0),
          feePolicy: amountIncludesFees ? "feesIncluded" : undefined,
        });
        return {
          type: PreparePaymentResultType.SEND_PREPARE,
          data: sendPrepare,
          amountIncludesFees: amountIncludesFees,
        };
      }
    }
  } catch (err: any) {
    if (err instanceof Error && err.message.startsWith("Invalid input: ")) {
      throw new Error(err.message.replace("Invalid input: ", ""));
    }
    if (err instanceof Error && err.message.startsWith("SparkSdkError: ")) {
      throw new Error(err.message.replace("SparkSdkError: ", ""));
    }

    if (err?.response?.data?.lockedFunds) {
      const untilDate = new Date(err.response.data.lockedFunds.until);
      const formattedDate = untilDate.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
      });

      throw new Error(`Your ${formatAmountWithSATS(err.response.data.lockedFunds.amountMsat / 1000)} will be available on ${formattedDate}`);
    } else if (err?.response?.data?.error) {
      throw new Error(err.response.data.error);
    } else {
      console.error("Error in preparePaymentFromTransactionState:", err);
      throw new Error("Unknown error");
    }
  }
};

export const payFromTransactionState = async (walletType: WalletType | null, transactionState: SendTransactionState, amountIncludesFees: boolean = false, identityPubkey?: string): Promise<Transaction | null> => {
  try {
    if(!walletType) {
      throw new Error("Wallet type not found");
    }

    switch(walletType) {
      case WalletType.SPARK:
        const breezSdk = getBreezSdk();

        if (!breezSdk) {
          throw new Error("Breez SDK not found");
        }

        if (transactionState.recipient?.bulkPayment) {
          let bulkResponse: BulkQuoteResponse | null = null;
          let bulkPatchedQuoteResponse: BulkQuoteResponse | null = null;

          if (transactionState.bulkQuoteResponse) {
            bulkResponse = transactionState.bulkQuoteResponse;

            bulkPatchedQuoteResponse = await walletRepository.patchBulkQuote(bulkResponse.invoice.id ?? "", {
              comment: transactionState.comment ?? "",
              anonymous: transactionState.anonymous ?? false,
              imageUrl: transactionState.imageUrl ?? undefined,
            });
          } else {
            const prepareResult = await preparePaymentFromTransactionState(transactionState);
            if (prepareResult.type !== PreparePaymentResultType.BULK_QUOTE) {
              throw new Error("Invalid prepare response for bulk payment");
            }
            bulkResponse = prepareResult.data;
          }

          const prepareResponse = await breezSdk.prepareSendPayment({
            paymentRequest: { type: "input", input: bulkResponse.invoice.invoice },
            amount: BigInt(bulkResponse.invoice.amountMsat / 1000),
          });
          const sendResponse = await breezSdk.sendPayment({
            prepareResponse: prepareResponse,
          });
          const transactionId = getTransactionIdFromSparkPayment(sendResponse.payment);
          const fakePendingTransaction = {
            ...bulkPatchedQuoteResponse?.fakePendingTransaction ?? bulkResponse.fakePendingTransaction,
            id: transactionId,
            paymentHash: transactionId,
          };
          return fakePendingTransaction;
        } else {
          let payment: Payment | null = null;
          if (transactionState.scanData?.parseResponse?.type === "lightningAddress") {
            const prepareResponse = await preparePaymentFromTransactionState(transactionState, amountIncludesFees);
            if (prepareResponse.type !== PreparePaymentResultType.LNURL_PREPARE) {
              throw new Error("Invalid prepare response for lnurl payment");
            }
            const sendResponse = await breezSdk.lnurlPay({
              prepareResponse: prepareResponse.data,
            });
            payment = sendResponse.payment;
          } else {
            const prepareResponse = await preparePaymentFromTransactionState(transactionState, amountIncludesFees);
            if (prepareResponse.type !== PreparePaymentResultType.SEND_PREPARE) {
              throw new Error("Invalid prepare response for send payment");
            }
            const sendResponse = await breezSdk.sendPayment({
              prepareResponse: prepareResponse.data,
            });
            payment = sendResponse.payment;
          }

          let paymentRequest: string = transactionState?.scanData?.paymentRequest ?? "";
          const breezResponse = payment;
          const paymentDetails = breezResponse.details;

          let paymentMethod: PaymentMethod | null = null;

          switch (paymentDetails?.type) {
            case 'spark':
              paymentMethod = PaymentMethod.SPARK;
              break;
            case 'lightning':
              if (paymentDetails.lnurlPayInfo?.lnAddress) {
                paymentRequest = paymentDetails.lnurlPayInfo.lnAddress;
                paymentMethod = PaymentMethod.LNURL;
              } else {
                paymentRequest = paymentDetails.invoice;
                paymentMethod = PaymentMethod.INVOICE;
              }
              break;
            case 'withdraw':
              paymentMethod = PaymentMethod.ONCHAIN;
              break;
          }

          let confirmResponse: Transaction | null = null;

          if(paymentMethod === PaymentMethod.ONCHAIN) {
            confirmResponse = sparkResponseToTransaction(breezResponse);
            try {
              await walletRepository.confirmSparkPayment({
                fromIdentityPubkey: identityPubkey,
                paymentRequest: paymentRequest,
                paymentMethod: paymentMethod,
                paymentId: breezResponse.id,
                paymentHash: getTransactionIdFromSparkPayment(breezResponse),
                amount: Number(breezResponse.amount),
                fees: Number(breezResponse.fees),
                timestamp: breezResponse.timestamp,
                comment: transactionState.comment ?? "",
                anonymous: transactionState.anonymous ?? false,
                imageUrl: transactionState.imageUrl ?? undefined,
                additionalInfos: transactionState.additionalInfos ?? undefined,
                btcTxId: confirmResponse.btcTxId ?? undefined,
              });
            } catch {}
          } else {
            confirmResponse = await walletRepository.confirmSparkPayment({
              fromIdentityPubkey: identityPubkey,
              paymentRequest: paymentRequest,
              paymentMethod: paymentMethod,
              paymentId: breezResponse.id,
              paymentHash: getTransactionIdFromSparkPayment(breezResponse),
              amount: Number(breezResponse.amount),
              fees: Number(breezResponse.fees),
              timestamp: breezResponse.timestamp,
              comment: transactionState.comment ?? "",
              anonymous: transactionState.anonymous ?? false,
              imageUrl: transactionState.imageUrl ?? undefined,
              additionalInfos: transactionState.additionalInfos ?? undefined,
            });
          }

          return confirmResponse;
        }
        break;
      case WalletType.INTERNAL:
        if (transactionState.recipient?.bulkPayment) {
          // Handle bulk payment
          const data = await walletRepository.payBulkPayment(
              transactionState.recipient.bulkPayment.type,
              transactionState.recipient.bulkPayment.id,
              transactionState.amount ?? 0,
              transactionState.comment ?? '',
              transactionState.anonymous ?? false,
              transactionState.membersOnly ?? false,
              transactionState.imageUrl ?? undefined,
              transactionState.recipient.bulkPayment.filters ?? undefined
          );
          
          return apiResponseToTransaction(data);
        } else {
            // Handle LNURL payment
            const data = await walletRepository.payLnurl(
                transactionState.scanData?.lnurl || transactionState.scanData?.invoice || '', 
                transactionState.amount ?? 0, 
                transactionState.comment ?? '',
                transactionState.recipient?.pleb?.id ?? undefined,
                transactionState.anonymous ?? false,
                transactionState.imageUrl ?? undefined
            );

            return apiResponseToTransaction(data);
        }
        break;
    }
  } catch (err: any) {
    console.error('Error in payFromTransactionState:', err);
    if (err instanceof Error && err.message.startsWith("SparkSdkError: ")) {
      let cleanedMessage = err.message
      .replace("SparkSdkError: ", "")
      .replace("Tree service error: ", "");

      throw new Error(cleanedMessage);
    }

    if (err?.response?.data?.lockedFunds) {
      const untilDate = new Date(err.response.data.lockedFunds.until);
      const formattedDate = untilDate.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
      });

      throw new Error(`Your ${formatAmountWithSATS(err.response.data.lockedFunds.amountMsat / 1000)} will be available on ${formattedDate}`);
    } else if (err?.response?.data?.error) {
      throw new Error(err.response.data.error);
    } else {
      console.error("Error in payFromTransactionState:", err);
      throw new Error("Unknown error");
    }
  }
};
